/* --------------------------------------------------------------------------
   New: stateful hook useLengthState
   - Keeps canonical meters and derived feet/inches synchronized.
   - Provides setters: setMeters, setFeetInches, setFeet, setInches.
   - Configurable inchesPrecision (decimal places for inches) and storagePrecisionM.
   -------------------------------------------------------------------------- */

import { useCallback, useMemo, useState } from "react";

export interface UseLengthConversionOptions {
    // how many decimals to allow for inches (0 => integer inches, 1 => 0.1", etc.)
    inchesPrecision?: number;
    // how many decimals to keep when storing meters (e.g. 3 => 0.001 m)
    storagePrecisionM?: number;
}

export type HeightUnit = "m" | "ft";

/** Exact conversion constants */
export const INCH_TO_M = 0.0254;
export const FOOT_TO_M = 0.3048;
export const M_TO_INCH = 1 / INCH_TO_M; // ~39.37007874015748
export const M_TO_FOOT = 1 / FOOT_TO_M; // ~3.280839895013123

/**
 * Hook that keeps canonical meters and derived feet/inches in sync.
 *
 * API (returned):
 * - meters: number | null
 * - setMeters(meters: number | null): void
 * - feet: number | null
 * - inches: number | null
 * - setFeetInches(feet: number | null, inches: number | null): void
 * - setFeet(feet: number | null): void
 * - setInches(inches: number | null): void
 * - toFeetInches(meters: number): { feet: number; inches: number }
 * - toMeters(feet: number, inches: number): number
 */
export function useLengthConversion(initialMeters: number | null = null, opts?: UseLengthConversionOptions) {
    const inchesPrecision = opts?.inchesPrecision ?? 0;
    const storagePrecisionM = opts?.storagePrecisionM ?? 3;

    const inchesFactor = Math.pow(10, inchesPrecision);

    // canonical meters stored in state (null means empty)
    const [meters, setMetersState] = useState<number | null>(initialMeters === null ? null : roundTo(initialMeters, storagePrecisionM));

    // round meters helper
    function roundTo(value: number, decimals: number) {
        const f = Math.pow(10, decimals);
        return Math.round(value * f) / f;
    }

    // pure helpers
    const toMeters = useCallback((feet: number, inches: number) => {
        const totalInches = feet * 12 + inches;
        const rawMeters = totalInches * INCH_TO_M;
        return roundTo(rawMeters, storagePrecisionM);
    }, [storagePrecisionM]);

    const toFeetInches = useCallback((m: number) => {
        const totalInches = m * M_TO_INCH;
        let feet = Math.floor(totalInches / 12);
        let inches = totalInches - feet * 12; // fractional inches

        // round inches to requested precision
        inches = Math.round(inches * inchesFactor) / inchesFactor;

        // normalize if inches rounded to 12 (or slightly above due to rounding)
        if (inches >= 12 - (0.5 / inchesFactor)) {
            feet += 1;
            inches = 0;
        }

        // When precision is zero, return integer inches
        if (inchesPrecision === 0) {
            inches = Math.round(inches);
        }

        return { feet, inches };
    }, [inchesFactor, inchesPrecision]);

    // derived feet/inches from current meters
    const derivedFeetInches = useMemo(() => {
        if (meters === null || meters === undefined) return { feet: null as number | null, inches: null as number | null };
        const { feet, inches } = toFeetInches(meters);
        return { feet, inches };
    }, [meters, toFeetInches]);

    // setter that accepts meters (null allowed)
    const setMeters = useCallback((m: number | null) => {
        if (m === null || m === undefined) {
            setMetersState(null);
            return;
        }
        setMetersState(roundTo(m, storagePrecisionM));
    }, [storagePrecisionM]);

    // setter that accepts feet + inches (either may be null)
    const setFeetInches = useCallback((feet: number | null, inches: number | null) => {
        if ((feet === null || feet === undefined) && (inches === null || inches === undefined)) {
            setMetersState(null);
            return;
        }
        const f = feet === null || feet === undefined ? 0 : Number(feet);
        const i = inches === null || inches === undefined ? 0 : Number(inches);
        if (Number.isNaN(f) || Number.isNaN(i)) {
            setMetersState(null);
            return;
        }
        // round totalInches to chosen inches precision before converting
        let totalInches = f * 12 + i;
        totalInches = Math.round(totalInches * inchesFactor) / inchesFactor;
        const m = roundTo(totalInches * INCH_TO_M, storagePrecisionM);
        setMetersState(m);
    }, [inchesFactor, storagePrecisionM]);

    // convenience single-value setters that preserve the other value (derived from current meters)
    const setFeet = useCallback((feet: number | null) => {
        const current = derivedFeetInches;
        const curFeet = current.feet ?? 0;
        const curInches = current.inches ?? 0;
        if (feet === null || feet === undefined) {
            // if clearing feet, but inches exist, treat as 0 feet
            setFeetInches(null, curInches);
            return;
        }
        setFeetInches(feet, curInches);
    }, [derivedFeetInches, setFeetInches]);

    const setInches = useCallback((inches: number | null) => {
        const current = derivedFeetInches;
        const curFeet = current.feet ?? 0;
        if (inches === null || inches === undefined) {
            setFeetInches(curFeet, null);
            return;
        }
        setFeetInches(curFeet, inches);
    }, [derivedFeetInches, setFeetInches]);

    return {
        // canonical meters
        meters,
        setMeters,

        // derived feet & inches (null if meters null)
        feet: derivedFeetInches.feet,
        inches: derivedFeetInches.inches,

        // setters
        setFeetInches,
        setFeet,
        setInches,

        // helpers (pure)
        toMeters,
        toFeetInches,

        // options readouts
        inchesPrecision,
        storagePrecisionM
    } as const;
}